import React, { useId, useMemo } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import { InformationBox, ProgressBar } from 'components';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  dropzone: {
    ...theme.fonts.caption,
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    position: 'relative',

    backgroundColor: theme.colors.grayBackground,
    width: '100%',
    minHeight: theme.marginBase * 16,

    padding: theme.marginBase * 3,
    borderRadius: theme.borderRadius.std,
    border: {
      style: 'solid',
      width: 1,
      color: 'transparent',
    },
    '&:hover': {
      border: {
        color: theme.colors.grayBorder,
      },
      '& .icon': {
        color: theme.colors.inkSecondary,
      },
    },
    transition: [['border-color', '.2s']],

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabled: {
    opacity: 0.5,
    '&:hover': {
      border: {
        color: 'transparent',
      },
      '& .icon': {
        color: theme.colors.inkTertiary,
      },
    },
  },
  hover: {
    borderColor: theme.colors.blue,
  },
  hoverRejected: {
    borderColor: theme.colors.red,
  },
  icon: {
    fontSize: theme.iconSize.large,
    color: theme.colors.inkTertiary,
  },
  label: {
    ...theme.fonts.label,
  },
  progressBar: {
    zIndex: 0,
    alignSelf: 'stretch',
    marginLeft: theme.marginBase * 3,
    marginRight: theme.marginBase * 3,
  },
  docxError: {
    marginTop: theme.marginBase,
  },
}));

interface Props extends DropzoneOptions {
  name?: string;
  inputProps?: Object;
  format: 'csv' | 'image' | 'pdf' | 'documents';
  label?: string;
  loaded?: number;
  total?: number;
  disabled?: boolean;
}

export const Dropzone = ({
  name,
  inputProps,
  label,
  format,
  loaded,
  total,
  disabled,
  ...dropzoneOptions
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const id = useId();
  const { t } = useTranslation();

  const acceptedFormats = {
    csv: {
      mime: {
        'text/csv': ['.csv'],
        'text/plain': ['.csv'],
        'text/x-csv': ['.csv'],
        'application/vnd.ms-excel': ['.csv'],
        'application/csv': ['.csv'],
        'application/x-csv': ['.csv'],
      },
      text: t('components.dropzone.formats.csv'),
    },
    image: {
      mime: { 'image/*': [] },
      text: t('components.dropzone.formats.image'),
    },
    pdf: {
      mime: { 'application/pdf': ['.pdf'] },
      text: t('components.dropzone.formats.pdf'),
    },
    documents: {
      mime: { 'application/pdf': ['.pdf'], 'image/*': [] },
      text: t('components.dropzone.formats.documents'),
    },
  };

  const formatInfo = acceptedFormats[format];

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    accept: formatInfo.mime,
    ...dropzoneOptions,
  });

  const showDocxError = useMemo(() => {
    if (format !== 'documents' && format !== 'pdf') {
      return false;
    }
    return fileRejections.some((rejection) =>
      /(docx?)|(odt)$/.test(rejection.file.name),
    );
  }, [format, fileRejections]);

  const rootProps = disabled ? {} : getRootProps();

  return (
    <React.Fragment>
      {!!label && (
        <label htmlFor={id} className={classes.label}>
          {label}
        </label>
      )}
      <div
        className={classnames(classes.dropzone, {
          [classes.hover]: isDragAccept,
          [classes.hoverRejected]: isDragReject,
          [classes.disabled]: disabled,
        })}
        {...rootProps}
      >
        <input
          {...getInputProps()}
          name={name}
          disabled={disabled}
          {...inputProps}
          id={id}
        />
        {isDragAccept ? (
          <p>
            <Trans i18nKey="components.dropzone.hover" />
          </p>
        ) : isDragReject ? (
          <p>
            <Trans i18nKey="components.dropzone.hoverReject" />
          </p>
        ) : (
          <React.Fragment>
            <div>
              <i
                className={classnames(
                  'ri-upload-cloud-2-line',
                  classes.icon,
                  'icon',
                )}
              />
            </div>
            {!loaded || !total ? (
              <p>
                <Trans
                  i18nKey="components.dropzone.content"
                  values={{ format: formatInfo.text }}
                />
              </p>
            ) : (
              <ProgressBar
                progression={loaded}
                fullSize={total}
                className={classes.progressBar}
              />
            )}
          </React.Fragment>
        )}
      </div>
      {!!fileRejections?.length && (
        <InformationBox variant="danger" className={classes.docxError}>
          {t('components.dropzone.badFormat', {
            context: showDocxError ? 'docx' : '',
            format: formatInfo.text,
          })}
        </InformationBox>
      )}
    </React.Fragment>
  );
};
